import {
    MotifAccordion,
    MotifAccordionContent,
    MotifAccordionTrigger,
    MotifButton,
    MotifButtonGroup
  } from "@ey-xd/motif-react";
  import ClaimSubmissionSummaryHeader from "./ClaimSubmissionSummaryHeader";
  import { useState } from "react";
  import { useTranslation } from "react-i18next";
  import { useParams } from "react-router-dom";
  import { useGetClaimSubmissionDetailsQuery } from "./claimSubmissionsApi";
  import "@/features/claimsummary/style.scss";
  
  const mockSummaries: Record<string, any> = {
    "SUBM-001": {
      creditor: {
        firstName: "Pete",
        lastName: "Samson",
        phone: "+1 987 654 3210",
        email: "pete.samson@example.com",
        address1: "456 Elm Street",
        address2: "Floor 3",
        country: "USA",
        city: "Los Angeles",
        provinceState: "CA",
        postalCode: "90001",
        lastUpdatedOn: "2024-12-01",
        lastUpdatedBy: "Admin",
      },
      assignee: {
        firstName: "John",
        lastName: "Doe",
        phone: "+1 234 567 8901",
        email: "john.doe@example.com",
        address1: "123 Main Street",
        address2: "Suite 456",
        country: "USA",
        city: "New York",
        provinceState: "NY",
        postalCode: "10001",
        lastUpdatedOn: "2024-12-02",
        lastUpdatedBy: "SupportUser",
      },
      documents: [
        {
          fileName: "Submission_Proof.pdf",
          fileType: "PDF",
          uploadedBy: "System",
          uploadedOn: "2024-12-12",
        },
      ],
    },
    "SUBM-002": {
      creditor: {
        firstName: "Jane",
        lastName: "Doe",
        phone: "+1 222 333 4444",
        email: "jane.doe@example.com",
        address1: "789 Maple Ave",
        address2: "",
        country: "USA",
        city: "Chicago",
        provinceState: "IL",
        postalCode: "60601",
        lastUpdatedOn: "2024-12-03",
        lastUpdatedBy: "SupportUser2",
      },
      assignee: {
        firstName: "Alice",
        lastName: "Smith",
        phone: "+1 333 444 5555",
        email: "alice.smith@example.com",
        address1: "987 Oak Blvd",
        address2: "",
        country: "USA",
        city: "Houston",
        provinceState: "TX",
        postalCode: "77001",
        lastUpdatedOn: "2024-12-04",
        lastUpdatedBy: "SupportUser3",
      },
      documents: [],
    },
  };
  
  export default function ClaimSubmissionSummary() {
    const { submissionId = "", caseId = "" } = useParams();
    const { data } = useGetClaimSubmissionDetailsQuery({
      caseId,
      submissionId,
    });
  
    const finalData = data ?? mockSummaries[submissionId];
  
    const { t } = useTranslation();
    const [creditorAccordianOpen, setCredtiorAccordianOpen] = useState(true);
    const [assigneeAccordianOpen, setAssigneeAccordianOpen] = useState(false);
    const [documentsAccordianOpen, setDocumentsAccordianOpen] = useState(false);
  
    const renderDetails = (info: any) => {
      if (!info) return <p>{t("global.globalNotifications.noRecords")}</p>;
  
      return (
        <ul className="ccwp_claim_summary_details">
          <li><label>{t("pages.claims.commonFieldLabels.firstName")}</label><p>{info.firstName}</p></li>
          <li><label>{t("pages.claims.commonFieldLabels.lastName")}</label><p>{info.lastName}</p></li>
          <li><label>{t("pages.claims.commonFieldLabels.phone")}</label><p>{info.phone}</p></li>
          <li><label>{t("pages.claims.commonFieldLabels.email")}</label><p>{info.email}</p></li>
          <li><label>{t("pages.claims.commonFieldLabels.address1")}</label><p>{info.address1}</p></li>
          <li><label>{t("pages.claims.commonFieldLabels.address2")}</label><p>{info.address2}</p></li>
          <li><label>{t("pages.claims.commonFieldLabels.country")}</label><p>{info.country}</p></li>
          <li><label>{t("pages.claims.commonFieldLabels.city")}</label><p>{info.city}</p></li>
          <li><label>{t("pages.claims.commonFieldLabels.provincestate")}</label><p>{info.provinceState}</p></li>
          <li><label>{t("pages.claims.commonFieldLabels.postalCode")}</label><p>{info.postalCode}</p></li>
          <li><label>{t("pages.claims.commonFieldLabels.lastUpdatedOn")}</label><p>{info.lastUpdatedOn}</p></li>
          <li><label>{t("pages.claims.commonFieldLabels.lastUpdatedBy")}</label><p>{info.lastUpdatedBy}</p></li>
        </ul>
      );
    };
  
    return (
      <>
        <ClaimSubmissionSummaryHeader
          caseId={caseId}
          submissionId={submissionId}
        />
  
        <MotifButtonGroup
          size="large"
          variant="secondary"
          className="justify-content-end pad-t-20"
        >
          <MotifButton variant="secondary">Received</MotifButton>
          <MotifButton variant="secondary">Reject</MotifButton>
          <MotifButton variant="secondary">On Hold</MotifButton>
          <MotifButton variant="secondary">Quarantine</MotifButton>
        </MotifButtonGroup>
  
        {/* Creditor Details */}
        <MotifAccordion
          className="mar-t-20"
          onClose={() => setCredtiorAccordianOpen(false)}
          onOpen={() => setCredtiorAccordianOpen(true)}
          alignIconRight
          useChevronIcon
          open={creditorAccordianOpen}
        >
          <MotifAccordionTrigger>
            {t("pages.submissionManagement.claimSubmissionView.creditorDetailsTitle")}
          </MotifAccordionTrigger>
          <MotifAccordionContent className="ccwp_claim_summary_accordian_wrapper">
            <div className="motif-container pad-t-10 pad-b-20 pad-l-40 ccwp_claim_summary_white_accordian">
              {renderDetails(finalData?.creditor)}
            </div>
          </MotifAccordionContent>
        </MotifAccordion>
  
        {/* Assignee Details */}
        <MotifAccordion
          className="mar-t-20"
          onClose={() => setAssigneeAccordianOpen(false)}
          onOpen={() => setAssigneeAccordianOpen(true)}
          alignIconRight
          useChevronIcon
          open={assigneeAccordianOpen}
        >
          <MotifAccordionTrigger>
            {t("pages.submissionManagement.claimSubmissionView.assigneeDetailsTitle")}
          </MotifAccordionTrigger>
          <MotifAccordionContent className="ccwp_claim_summary_accordian_wrapper">
            <div className="motif-container pad-t-10 pad-b-20 pad-l-40 ccwp_claim_summary_white_accordian">
              {renderDetails(finalData?.assignee)}
            </div>
          </MotifAccordionContent>
        </MotifAccordion>
  
        {/* Documents */}
        <MotifAccordion
          className="mar-t-20"
          onClose={() => setDocumentsAccordianOpen(false)}
          onOpen={() => setDocumentsAccordianOpen(true)}
          alignIconRight
          useChevronIcon
          open={documentsAccordianOpen}
        >
          <MotifAccordionTrigger>
            {t("pages.submissionManagement.claimSubmissionView.documentsDetailsTitle")}
          </MotifAccordionTrigger>
          <MotifAccordionContent className="ccwp_claim_summary_accordian_wrapper">
                        <div className="motif-container pad-b-20 pad-l-40 ccwp_claim_summary_white_accordian">
                            <div className="motif-row">
                                <p>No document found!</p>
                            </div>
                        </div>
                    </MotifAccordionContent>
        </MotifAccordion>
      </>
    );
  }
  